import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Modal from '../common/Modal';
import FormField from '../common/FormField';
import { Button } from '../../design-system/components/Button';
import { Stack } from '../../design-system/components/Layout';
import { Payment, SubscriptionCategory } from '../../types/payment';
import { spacing } from '../../design-system/tokens/spacing';

const FormContainer = styled.div`
  padding: ${spacing[6]};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
  margin-top: ${spacing[6]};
  padding-top: ${spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

interface PaymentStatusModalProps {
  payment: Payment;
  onClose: () => void;
  onSave: (request: {
    status: number;
    paymentMethod?: string;
    transactionReference?: string;
    notes?: string;
    paidAt?: string;
  }) => void;
}

const subscriptionCategoryKeys: Record<number, string> = {
  1: 'payments.subscriptionCategoryRegular',
  2: 'payments.subscriptionCategoryStudent',
  3: 'payments.subscriptionCategoryRetired',
  4: 'payments.subscriptionCategoryHonorary',
  10: 'payments.subscriptionCategoryInstitution1',
  11: 'payments.subscriptionCategoryInstitution2',
  12: 'payments.subscriptionCategoryInstitution3',
};

const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
  payment,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const getSubscriptionCategoryLabel = (cat: SubscriptionCategory) =>
    t(subscriptionCategoryKeys[cat as number] || `payments.subscriptionCategory`);
  const [formData, setFormData] = useState({
    paymentMethod: '',
    transactionReference: '',
    notes: '',
    paidAt: new Date().toISOString().split('T')[0],
  });
  const [paymentMethodError, setPaymentMethodError] = useState('');

  const paymentMethodOptions = [
    { value: '', label: t('payments.paymentMethodSelect') },
    { value: 'Bank Deposit', label: t('payments.paymentMethodBankDeposit') },
    { value: 'Cash', label: t('payments.paymentMethodCash') },
    { value: 'POS', label: t('payments.paymentMethodPos') },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.paymentMethod) {
      setPaymentMethodError(t('payments.paymentMethodRequired'));
      return;
    }
    setPaymentMethodError('');
    onSave({
      status: 2, // Paid
      paymentMethod: formData.paymentMethod,
      transactionReference: formData.transactionReference || undefined,
      notes: formData.notes || undefined,
      paidAt: formData.paidAt || undefined,
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('admin.payments.markAsPaid')}
    >
      <FormContainer>
        <form onSubmit={handleSubmit}>
          <Stack direction="column" spacing="md">
            <div style={{ padding: spacing[4], background: 'rgba(0, 0, 0, 0.05)', borderRadius: '8px', marginBottom: spacing[4] }}>
              <strong>{t('payments.member')}:</strong> {payment.memberName}<br />
              <strong>{t('payments.year')}:</strong> {payment.year}<br />
              <strong>{t('payments.amount')}:</strong> €{payment.amount.toFixed(2)}
              {payment.subscriptionCategory != null && (
                <>
                  <br />
                  <strong>{t('payments.subscriptionCategory')}:</strong> {getSubscriptionCategoryLabel(payment.subscriptionCategory)}
                </>
              )}
            </div>

            <FormField
              id="mark-paid-payment-method"
              name="paymentMethod"
              label={t('payments.paymentMethod')}
              select
              required
              value={formData.paymentMethod}
              onChange={(e) => {
                handleChange('paymentMethod', e.target.value);
                if (e.target.value) setPaymentMethodError('');
              }}
              options={paymentMethodOptions}
              error={paymentMethodError}
            />

            <FormField
              label={t('payments.transactionReference')}
              type="text"
              value={formData.transactionReference}
              onChange={(e) => handleChange('transactionReference', e.target.value)}
              placeholder={t('payments.transactionReferencePlaceholder')}
            />

            <FormField
              label={t('payments.paidAt')}
              type="date"
              value={formData.paidAt}
              onChange={(e) => handleChange('paidAt', e.target.value)}
              required
            />

            <FormField
              label={t('payments.notes')}
              type="textarea"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder={t('payments.notesPlaceholder')}
            />

            <ActionsRow>
              <Button variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button variant="gradient" type="submit">
                {t('admin.payments.markAsPaid')}
              </Button>
            </ActionsRow>
          </Stack>
        </form>
      </FormContainer>
    </Modal>
  );
};

export default PaymentStatusModal;
